/**
 * Agent-orchestration signal embedded in assistant prose (P3-024).
 * When {@link CONVERSATION_END_MARKER} appears in a reply, the widget
 * shows end-of-chat overall feedback. The marker is stripped from the
 * rendered transcript — see `planning/API-CONTRACT.md` §5.3.1.
 *
 * The widget parses every `complete` frame even when the API already
 * stripped the marker, so mock harnesses and future agent paths stay
 * compatible without requiring a server deploy lockstep.
 */
export const CONVERSATION_END_MARKER = 'conversation-end';

export interface ParsedConversationEndMessage {
  /** Text safe to render in the transcript bubble. */
  displayText: string;
  /** Whether this turn should surface overall experience feedback. */
  isConversationEnd: boolean;
}

/**
 * Detect and remove the conversation-end marker from assistant prose.
 * Case-sensitive — must match {@link CONVERSATION_END_MARKER} exactly.
 */
export function parseConversationEndMessage(
  raw: string
): ParsedConversationEndMessage {
  if (!raw.includes(CONVERSATION_END_MARKER)) {
    return { displayText: raw, isConversationEnd: false };
  }

  return {
    displayText: raw.replaceAll(CONVERSATION_END_MARKER, '').trim(),
    isConversationEnd: true,
  };
}

/**
 * A short "no" that answers "would you like to return …?" is not the
 * customer ending the chat. Mirrors `ConversationEndPolicy` in Core
 * (WR3NCH-4022). "No, I'm done" and "End Conversation" are excluded.
 */
export function isDeclineOfReturnQuestion(
  userMessage: string | undefined,
  previousAssistantText: string | undefined
): boolean {
  if (
    userMessage === undefined ||
    previousAssistantText === undefined ||
    !previousAssistantText.includes('?') ||
    !previousAssistantText.toLowerCase().includes('return')
  ) {
    return false;
  }

  const text = userMessage.trim().replace(/[.!]+$/, '');
  if (text.length === 0 || text.length > 80) {
    return false;
  }

  const lower = text.toLowerCase();
  if (
    lower.includes('done') ||
    lower.includes("that's all") ||
    lower.includes('thats all') ||
    lower.includes('nothing else') ||
    lower.includes('end conversation') ||
    lower.includes('all set')
  ) {
    return false;
  }

  return (
    lower === 'no' ||
    lower === 'nope' ||
    lower === 'nah' ||
    lower === 'no thanks' ||
    lower === 'no thank you' ||
    lower === 'not this' ||
    lower === 'not this one' ||
    lower.startsWith('no ') ||
    lower.startsWith('no,')
  );
}
